import React from 'react';
import Navigation from './Navigation';
import Footer from './Footer';

const Blog = () => {
  return (
    <div className="min-h-screen bg-[#050505] font-inter">
      <Navigation />
      
      {/* Blog Post Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        {/* Header */}
        <header className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Going to a Gig Alone in London? Stop Missing Out. Here's the New Way to Do It.
          </h1>
          <div className="flex items-center gap-4 text-gray-400 text-sm">
            <time dateTime="2024">Published 2026</time>
            <span>•</span>
            <span>5 min read</span>
          </div>
        </header>

        {/* Blog Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          <div className="text-gray-300 leading-relaxed space-y-6">
            <p className="text-lg sm:text-xl text-gray-200">
              It happens every time a tour is announced. You see the poster: your favourite band is playing O2 Academy Brixton or the Roundhouse in February.
            </p>

            <p className="text-lg sm:text-xl text-gray-200">
              The adrenaline spikes. You rush to Dice or See Tickets. You secure the ticket.
            </p>

            <p className="text-lg sm:text-xl text-gray-200">
              Then, the realization hits.
            </p>

            <p className="text-lg sm:text-xl text-gray-200 font-semibold">
              Who am I actually going with?
            </p>

            <p className="text-lg sm:text-xl text-gray-200">
              Your usual concert mate is broke. Your partner hates indie music. Your other friends are "busy." You are left with two choices: miss the gig entirely, or brave it solo in one of the biggest, busiest cities in the world.
            </p>

            <p className="text-lg sm:text-xl text-gray-200">
              If you're reading this, you've probably typed "Is it weird to go to a concert alone in London?" into Google recently.
            </p>

            <p className="text-lg sm:text-xl text-gray-200">
              Here is the honest truth about the London solo gig experience, the safety realities, and the new movement changing how individual fans experience live music.
            </p>

            {/* Section: The Truth */}
            <section className="mt-12 pt-8 border-t border-gray-800">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                The Truth: Nobody Cares You Are Alone (When the Music Starts)
              </h2>
              <p className="text-lg sm:text-xl text-gray-200">
                Let's validate something first: going to a gig alone is arguably the best way to experience music.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                You don't have to compromise on who to see. You don't have to debate whether to stand by the sound desk or fight for the barrier. You can leave when you want. It is pure, unfiltered musical selfishness, and it's glorious.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                Once the lights go down and the headliner walks on, nobody is looking at you. Everyone is looking at the stage. You are surrounded by 2,000 people who love exactly what you love. You are not alone.
              </p>
            </section>

            {/* Section: The Problem */}
            <section className="mt-12 pt-8 border-t border-gray-800">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                The Problem: The "Gap" Between Sets
              </h2>
              <p className="text-lg sm:text-xl text-gray-200">
                If the music part is easy, why do so many of us hesitate?
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                It's the "Gap." It's the 45 minutes between the support act finishing and the main act starting.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                It's standing at the bar at Alexandra Palace, furiously scrolling through your phone so you look busy. It's the inability to save your spot when you need the toilet.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                And, crucially, for many Londoners—especially women and marginalized groups—it's the safety calculation. It's wondering if the person trying to talk to you is genuine or a creep. It's the walk to the tube station at 11:30 PM on a Friday night in an unfamiliar borough.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                We love the music, but we hate the vulnerability.
              </p>
            </section>

            {/* Section: Why the Old Solutions Don't Work */}
            <section className="mt-12 pt-8 border-t border-gray-800">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Why the Old Solutions Don't Work
              </h2>
              <p className="text-lg sm:text-xl text-gray-200">
                For years, solo fans have tried to hack together solutions to bridge this gap:
              </p>
              <ul className="list-disc list-inside space-y-4 text-lg sm:text-xl text-gray-200 ml-4">
                <li>
                  <strong className="text-white">The Reddit Threads:</strong> Posting in r/LondonSocialClub hoping a stranger replies. It sometimes works, but it's a total roll of the dice on who turns up.
                </li>
                <li>
                  <strong className="text-white">The Dating Apps:</strong> Trying to convert a Hinge match into a gig buddy. It rarely works and adds unnecessary romantic pressure to a night out.
                </li>
                <li>
                  <strong className="text-white">The Facebook Groups:</strong> Huge, unmanaged groups with thousands of members, where posts get lost in the noise.
                </li>
              </ul>
              <p className="text-lg sm:text-xl text-gray-200 mt-6">
                None of these solve the core issue: Trust. How do you know the person you are meeting is safe, real, and actually there for the music?
              </p>
            </section>

            {/* Section: Enter the "Verified Squad" Movement */}
            <section className="mt-12 pt-8 border-t border-gray-800">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Enter the "Verified Squad" Movement
              </h2>
              <p className="text-lg sm:text-xl text-gray-200">
                We got tired of missing gigs because our friends flaked. We also got tired of the anxiety of meeting unverified strangers from the internet.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                London's indie scene needed an upgrade. That's why we are building CrowdOne.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                We are not a dating app. We aren't a massive social network. We are a tactical tool for solo music fans to form temporary, safe "Squads" for specific gigs.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                We are solving the "Gap" by introducing three non-negotiables to the solo experience:
              </p>

              <div className="mt-8 space-y-6">
                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    1. Safety isn't optional (ID Verification)
                  </h3>
                  <p className="text-lg text-gray-200">
                    You shouldn't have to guess if someone is real. On CrowdOne, every single member must verify their Government ID (via Stripe Identity) to enter the community. This immediately filters out bots, catfish, and bad actors.
                  </p>
                </div>

                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    2. The Vibe Check (Small Squads)
                  </h3>
                  <p className="text-lg text-gray-200">
                    Nobody wants to meet 50 people at once. We place you in a small chat group (4-6 people) 24 hours before the gig. It's enough people to feel safe, but few enough to actually have a conversation over a pre-gig pint.
                  </p>
                </div>

                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    3. The "Digital Totem"
                  </h3>
                  <p className="text-lg text-gray-200">
                    "Meet by the bar" is useless instructions at a sold-out Wembley Arena show. We provide a pinned, exact meeting location on the venue floor plan inside the app chat. You know exactly where to go and who to look for.
                  </p>
                </div>
              </div>
            </section>

            {/* Section: Don't Miss the Spring Season */}
            <section className="mt-12 pt-8 border-t border-gray-800">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Don't Miss the Spring Season
              </h2>
              <p className="text-lg sm:text-xl text-gray-200">
                The London spring calendar is stacked. Fontaines D.C., Wolf Alice, The Wombats—the list is huge.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                Don't let the fear of going alone stop you from buying that ticket. The music is too good to miss.
              </p>
              <p className="text-lg sm:text-xl text-gray-200">
                We are currently running private pilots for select London indie gigs before our public launch. If you want to be matched with a verified squad for an upcoming show, you need to get on the list now.
              </p>
            </section>

            {/* FAQ Section */}
            <section className="mt-12 pt-8 border-t border-gray-800">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">
                Frequently Asked Questions about Solo Gigs
              </h2>
              
              <div className="space-y-8">
                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    Is it safe to go to a concert alone in London?
                  </h3>
                  <p className="text-lg text-gray-200">
                    Generally, yes. London venues like the O2 Academy Brixton, The Roundhouse, and Eventim Apollo have strong security and crowd management. However, travelling to and from the venue late at night can feel vulnerable. This is why many solo fans prefer to meet a "Squad" beforehand—it ensures you have people to walk to the tube station with after the encore.
                  </p>
                </div>

                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    How does CrowdOne verify that users are real?
                  </h3>
                  <p className="text-lg text-gray-200">
                    We take safety seriously. Unlike Facebook groups or Reddit threads, CrowdOne requires Government ID Verification (processed securely via Stripe Identity) or a verifiable payment method before a user can enter a Squad Chat. This ensures that the person you are chatting with is who they say they are.
                  </p>
                </div>

                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    Will it be awkward meeting strangers?
                  </h3>
                  <p className="text-lg text-gray-200">
                    We designed the app to kill the awkwardness. You aren't meeting for a "date" or a long dinner; you are meeting for the music. We match you into small groups (4-6 people) based on the specific gig you are attending. Because you already have a shared obsession (the band), the conversation flows naturally.
                  </p>
                </div>

                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    What happens if I don't click with my squad?
                  </h3>
                  <p className="text-lg text-gray-200">
                    There is zero obligation. CrowdOne is a tool to facilitate the introduction, not a marriage contract. If you meet up for a pre-drink and want to head into the crowd on your own later, that is totally fine. You use the squad as much or as little as you need.
                  </p>
                </div>

                <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    How do I find my squad at the venue?
                  </h3>
                  <p className="text-lg text-gray-200">
                    "Meet by the bar" is a nightmare at sold-out shows. We use a feature called the Digital Totem—a specific, pinned location on the venue's floorplan (e.g., "Front Left, by the Sound Desk"). You can see exactly where your group is on the in-app map.
                  </p>
                </div>
              </div>
            </section>

            {/* Call to Action */}
            <section className="mt-12 pt-8 border-t border-gray-800">
              <p className="text-lg sm:text-xl text-gray-200 mb-6">
                Ready to find your people?
              </p>
              <div className="mt-8">
                <a
                  href="/"
                  className="inline-block bg-[#4ADE80] hover:bg-[#3bc96a] text-[#050505] font-bold py-3 px-8 rounded-lg transition-colors duration-200 text-lg"
                >
                  Join the CrowdOne Pilot List here
                </a>
              </div>
              <p className="text-lg sm:text-xl text-gray-200 mt-6 font-semibold">
                Never stand alone at the barrier again.
              </p>
            </section>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default Blog;
